import { Button } from "@/components/ui/baseUi/button"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { BoxSlotSessionContentDetails } from "@/modules/boxSlotSession/components/boxSlotSessionContentDetails"
import { useBoxSlotFetchSession } from "@/modules/boxSlotSession/hooks/useBoxSlotGetSession"
import { cn } from "@/modules/lib/utils"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { defaultDateFormat } from "@/modules/utils/dateUtils"
import SessionEmpty from "@images/empty/session_empty.svg"
import { useBoxSlotDetails } from "@modules/boxSlot/hooks/useBoxSlotDetails"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { EmptyView } from "@modules/ui/components/emptyView"
import dayjs from "dayjs"
import { Plus } from "lucide-react"
import { useMemo } from "react"

type BoxSlotSessionContentInfoProps = {
  boxSlotId: string
} & React.ComponentProps<"div">

export function BoxSlotSessionContentInfo({ boxSlotId, className, ...props }: BoxSlotSessionContentInfoProps) {
  const { data: boxSlot } = useBoxSlotDetails(boxSlotId)
  const { data: session } = useBoxSlotFetchSession(boxSlot.sessionId)
  const { data: boxProgrammation } = useBoxProgram(boxSlot.programmationId)
  const canEditSession = useBoxStaffPermission(BoxStaffPermissions.MANAGE_PROGRAMMING)
  const { navigateToSessionForm } = useSessionSidebar()

  const editSession = useMemo(
    () => () => {
      if (boxSlot.sessionId) {
        void navigateToSessionForm(boxProgrammation, session, undefined, boxSlotId, { replaceAllComponents: false })
      } else {
        void navigateToSessionForm(
          boxProgrammation,
          undefined,
          dayjs(boxSlot.startDateTime).format(defaultDateFormat),
          boxSlotId,
          { replaceAllComponents: false },
        )
      }
    },
    [boxSlot, boxProgrammation, session, navigateToSessionForm, boxSlotId],
  )

  return (
    <div className={cn("flex grow flex-col gap-2 px-5 pt-4", className)} {...props}>
      {boxSlot.sessionId ? (
        <BoxSlotSessionContentDetails boxSessionId={boxSlot.sessionId} boxSlotId={boxSlotId} />
      ) : (
        <EmptyView
          image={{ src: SessionEmpty }}
          title={word("box.slot.detail.exercise.empty.title")}
          description={word("box.slot.detail.exercise.empty.description")}
          button={
            canEditSession ? (
              <Button variant="primary" onClick={editSession}>
                <Plus />
                {word("box.slot.detail.subscriber.empty.button")}
              </Button>
            ) : null
          }
          paddingType="Default"
        />
      )}
    </div>
  )
}
